// src/components/ExportButton.js

import { useState } from 'react';
import { Download, FileSpreadsheet, Check } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useTheme } from '../context/ThemeContext';

function ExportButton({ shipments, excursions }) {
  const { colors } = useTheme();
  const [isExporting, setIsExporting] = useState(false);
  const [exported, setExported] = useState(false);

  const handleExport = () => {
    setIsExporting(true);

    try {
      // Prepare shipments data
      const shipmentsData = shipments.map(s => ({
        'Shipment ID': s.id,
        'Product': s.productName,
        'Product Type': s.productType,
        'Status': s.status,
        'Carrier': s.carrierName,
        'Origin': `${s.originName || s.originCity || 'N/A'}, ${s.originState || ''}`,
        'Destination': `${s.destinationName || s.destinationCity || 'N/A'}, ${s.destinationState || ''}`,
        'Ship Date': s.shipDate ? new Date(s.shipDate).toLocaleDateString() : 'N/A',
        'Temp Min (°C)': s.tempMin,
        'Temp Max (°C)': s.tempMax,
        'Units': s.units,
        'Total Value ($)': s.totalValue,
        'Transit Hours': s.transitHours,
        'Has Excursion': s.hasExcursion ? 'Yes' : 'No',
        'Excursion Severity': s.excursionSeverity || 'N/A',
        'Stability Budget Used (%)': s.stabilityBudgetUsed?.toFixed(1) || 'N/A',
      }));

      // Prepare excursions data
      const excursionsData = excursions.map(e => {
        const shipment = shipments.find(s => s.id === e.shipmentId);
        return {
          'Excursion ID': e.id,
          'Shipment ID': e.shipmentId,
          'Product': shipment?.productName || 'N/A',
          'Severity': e.severity,
          'Duration (min)': e.durationMinutes,
          'Max Deviation (°C)': e.maxDeviationC,
          'Root Cause': e.rootCause,
          'Value at Risk ($)': e.valueAtRisk,
          'Product Lost': e.productLost ? 'Yes' : 'No',
          'Start Time': e.startTime ? new Date(e.startTime).toLocaleString() : 'N/A',
        };
      });

      // Create summary data
      const totalShipments = shipments.length;
      const delivered = shipments.filter(s => s.status === 'Delivered').length;
      const inTransit = shipments.filter(s => s.status === 'In Transit').length;
      const withExcursions = shipments.filter(s => s.hasExcursion).length;
      const criticalCount = excursions.filter(e => e.severity === 'Critical').length;
      const majorCount = excursions.filter(e => e.severity === 'Major').length;
      const minorCount = excursions.filter(e => e.severity === 'Minor').length;
      const totalValueAtRisk = excursions.reduce((sum, e) => sum + (e.valueAtRisk || 0), 0);
      const complianceScore = Math.round(((totalShipments - withExcursions) / totalShipments) * 100);

      const summaryData = [
        { 'Metric': 'Total Shipments', 'Value': totalShipments },
        { 'Metric': 'Delivered', 'Value': delivered },
        { 'Metric': 'In Transit', 'Value': inTransit },
        { 'Metric': 'Shipments with Excursions', 'Value': withExcursions },
        { 'Metric': 'GDP Compliance Score', 'Value': `${complianceScore}%` },
        { 'Metric': 'Critical Excursions', 'Value': criticalCount },
        { 'Metric': 'Major Excursions', 'Value': majorCount },
        { 'Metric': 'Minor Excursions', 'Value': minorCount },
        { 'Metric': 'Total Value at Risk', 'Value': `$${totalValueAtRisk.toLocaleString()}` },
        { 'Metric': 'Report Generated', 'Value': new Date().toLocaleString() },
      ];

      // Create workbook with multiple sheets
      const wb = XLSX.utils.book_new();

      // Add Summary sheet
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      summaryWs['!cols'] = [{ wch: 25 }, { wch: 20 }];
      XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

      // Add Shipments sheet
      const shipmentsWs = XLSX.utils.json_to_sheet(shipmentsData);
      shipmentsWs['!cols'] = [
        { wch: 18 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 20 },
        { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 12 },
        { wch: 8 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(wb, shipmentsWs, 'Shipments');

      // Add Excursions sheet
      const excursionsWs = XLSX.utils.json_to_sheet(excursionsData);
      excursionsWs['!cols'] = [
        { wch: 18 }, { wch: 18 }, { wch: 20 }, { wch: 10 }, { wch: 14 },
        { wch: 18 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 20 }
      ];
      XLSX.utils.book_append_sheet(wb, excursionsWs, 'Excursions');

      // Generate filename with date
      const dateStr = new Date().toISOString().split('T')[0];
      const filename = `ChainGuard_Export_${dateStr}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);

      setExported(true);
      setTimeout(() => setExported(false), 2000);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        padding: '0.6rem 1rem',
        background: exported ? colors.success : colors.bgElevated,
        border: `1px solid ${exported ? colors.success : colors.border}`,
        borderRadius: '8px',
        color: exported ? 'white' : colors.textPrimary,
        fontSize: '0.85rem',
        fontWeight: 500,
        cursor: isExporting ? 'wait' : 'pointer',
        transition: 'all 0.2s ease',
      }}
      onMouseEnter={(e) => {
        if (!exported && !isExporting) {
          e.currentTarget.style.borderColor = colors.primary;
          e.currentTarget.style.color = colors.primary;
        }
      }}
      onMouseLeave={(e) => {
        if (!exported && !isExporting) {
          e.currentTarget.style.borderColor = colors.border;
          e.currentTarget.style.color = colors.textPrimary;
        }
      }}
    >
      {exported ? (
        <>
          <Check size={16} />
          Exported!
        </>
      ) : isExporting ? (
        <>
          <FileSpreadsheet size={16} />
          Exporting...
        </>
      ) : (
        <>
          <Download size={16} />
          Export Excel
        </>
      )}
    </button>
  );
}

export default ExportButton;